// Reusable inline-help affordance. Renders a small "?" button that
// reveals a short explanation popover when clicked. Use it next to a
// feature when (a) the feature is non-obvious AND (b) the explanation
// is short enough that mounting it inline is cheaper than reading the
// whole mode-level help. For mode-level overviews keep using
// `HelpPopover` in the sidebar.
//
// Design notes:
//   - Click to toggle (not hover) -- hover popovers are awkward on
//     touch + screen-reader unfriendly. The button itself has a
//     descriptive `aria-label` so the affordance is discoverable
//     without seeing the panel.
//   - Click-outside dismisses (mousedown listener, capture phase so
//     it fires before the trigger button's own click rehydrates the
//     popover).
//   - Renders the panel below the trigger by default. Pass
//     `placement="left"` when the feature lives at the right edge
//     so the panel doesn't clip viewport.
//   - The panel is intentionally sized small (max-w-xs) -- if the
//     copy doesn't fit, the feature probably needs mode-level help
//     instead of inline help.
//   - When `dismissKey` is set, the popover renders a "Don't show
//     again" footer link. Clicking it persists a flag in
//     localStorage and unmounts the trigger so the affordance no
//     longer takes up space next to the feature. Storage events are
//     observed cross-tab so dismissing on one tab updates the
//     others without a reload. (Reported via dev-sync 62878f84.)
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  inlineHelpDismissStorageKey,
  readInlineHelpDismissed,
  writeInlineHelpDismissed,
} from "./inlineHelpDismissStorage";
import { BB_UI_PREFS_HYDRATED_EVENT, patchUserUiPrefs } from "@/lib/userUiPrefs";

type Placement = "left" | "right";
type Glyph = "?" | "!";

type Props = {
  /** Short hover/screen-reader label like "What is alias learning?". */
  label: string;
  /** Inline content shown when the popover opens. Keep it under ~4
   *  sentences -- longer copy belongs in the mode-level help. */
  children: ReactNode;
  /** Right-aligned panel (anchored to the trigger's right edge) when
   *  the feature lives near the viewport's right edge. */
  placement?: Placement;
  /** Optional extra classes for the trigger button (e.g. to align it
   *  with the surrounding text baseline). */
  triggerClassName?: string;
  /** When set, the popover gains a "Don't show again" footer link.
   *  Persisting the dismissal hides the trigger entirely on
   *  subsequent mounts. Pick stable, descriptive keys (e.g.
   *  `mfg.receipts.shipTax`) so the registry is grep-able. */
  dismissKey?: string;
  /** Trigger glyph. Defaults to "?" (canonical inline help). Use "!"
   *  for incidental-info popovers like timestamps that don't deserve
   *  their own card slot but should be reachable on demand. The
   *  popover chrome and dismiss-affordance behavior are identical
   *  across glyphs; only the trigger character differs. */
  glyph?: Glyph;
};

export function InlineHelp({
  label,
  children,
  placement = "right",
  triggerClassName = "",
  dismissKey,
  glyph = "?",
}: Props) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => readInlineHelpDismissed(dismissKey));
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, [open]);

  // Cross-tab + same-tab consistency: if the user dismisses this
  // affordance from a different surface (or another tab) we want
  // every mounted trigger with the same key to disappear without a
  // reload. Storage events fire only across tabs, so we also re-read
  // when `dismissKey` changes (covers prop swaps in tests).
  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(readInlineHelpDismissed(dismissKey));
    if (typeof window === "undefined") return;
    const storageKey = inlineHelpDismissStorageKey(dismissKey);
    function onStorage(e: StorageEvent) {
      if (e.key !== storageKey) return;
      setDismissed(e.newValue === "1");
    }
    window.addEventListener("storage", onStorage);
    const onHydrated = () => setDismissed(readInlineHelpDismissed(dismissKey));
    window.addEventListener(BB_UI_PREFS_HYDRATED_EVENT, onHydrated);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(BB_UI_PREFS_HYDRATED_EVENT, onHydrated);
    };
  }, [dismissKey]);

  if (dismissed) return null;

  return (
    <span ref={rootRef} className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 bg-white text-[10px] font-semibold leading-none text-gray-600 hover:border-indigo-400 hover:text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:text-indigo-300 ${triggerClassName}`}
      >
        {glyph}
      </button>
      {open ? (
        <span
          role="dialog"
          aria-label={label}
          className={`absolute top-full z-30 mt-1 max-w-xs rounded-md border border-gray-200 bg-white p-2.5 text-[11px] leading-relaxed text-gray-700 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 ${
            placement === "left" ? "right-0" : "left-0"
          }`}
          style={{ width: "max-content", minWidth: "14rem" }}
        >
          {children}
          {dismissKey ? (
            <span className="mt-2 flex justify-end border-t border-gray-100 pt-2 dark:border-zinc-700">
              <button
                type="button"
                onClick={() => {
                  writeInlineHelpDismissed(dismissKey);
                  void patchUserUiPrefs({ inlineHelpDismissed: { [dismissKey]: true } });
                  setDismissed(true);
                  setOpen(false);
                }}
                className="text-[10px] font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
              >
                Don&rsquo;t show again
              </button>
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
